/**
 * ロジスティック回帰（Logistic Regression）
 * 計算式は docs/systems/keisuke/FORMULAS.md を参照
 */

import { KeisukeAdapterError } from '../keisukeAdapterI18n';

import { linearRegression, type LinearRegressionResult } from './regression';

/**
 * ロジット変換
 * Logit = ln(p / (1 - p))
 * 
 * @param p 死亡率または効果率（0-1の範囲）
 * @returns ロジット値
 */
function logitTransform(p: number): number {
  if (p <= 0 || p >= 1) {
    // 0%または100%の場合は極値を使用
    if (p <= 0) {
      p = 0.001;
    } else {
      p = 0.999;
    }
  }

  return Math.log(p / (1 - p));
}

/**
 * ロジスティック回帰の結果
 */
export interface LogisticResult {
  slope: number;              // 傾き（b）
  intercept: number;          // 切片（a）
  ld50: number;              // LD50値
  ec50: number;              // EC50値
  ec90: number;              // EC90値
  ld50CI: { lower: number; upper: number };  // LD50の95%信頼区間
  ec50CI: { lower: number; upper: number };  // EC50の95%信頼区間
  ec90CI: { lower: number; upper: number };  // EC90の95%信頼区間
  regression: LinearRegressionResult;  // 線形回帰の結果
  isGLM?: boolean;            // GLM（二項分布 + ロジットリンク）を使用したかどうか
}

/**
 * ロジスティック回帰によるLD50/EC50/EC90の計算
 * 
 * 簡易実装: ロジット変換 + 線形回帰
 * 完全な最尤推定は今後の拡張として実装
 * 
 * @param doses 用量（濃度）の配列
 * @param responses 死亡率または効果率（0-1の範囲）の配列
 * @returns ロジスティック回帰の結果
 */
export function logisticRegression(
  doses: number[],
  responses: number[]
): LogisticResult {
  if (doses.length !== responses.length || doses.length < 3) {
    throw new KeisukeAdapterError('minThreeDataPoints');
  }

  // 用量と反応率を対数変換とロジット変換
  const logDoses = doses.map(d => Math.log10(d));
  const logits = responses.map(p => logitTransform(p));

  // 線形回帰: Logit = a + b * log10(dose)
  const regression = linearRegression(logDoses, logits);

  const a = regression.intercept;  // 切片
  const b = regression.slope;      // 傾き

  if (Math.abs(b) < 1e-10) {
    throw new KeisukeAdapterError('slopeNearZero');
  }

  // LD50/EC50の計算: LD50/EC50 = 10^(-a/b)
  // ロジット値0が50%に対応
  const ld50 = Math.pow(10, -a / b);
  const ec50 = ld50;  // LD50とEC50は同じ計算式

  // EC90の計算: EC90 = 10^(-(a + ln(9))/b)
  // ロジット値ln(9)が90%に対応
  const logit90 = Math.log(9);  // ln(0.9 / 0.1) = ln(9)
  const ec90 = Math.pow(10, -(a + logit90) / b);

  // 信頼区間の計算（簡易版：回帰の標準誤差を使用）
  const se = regression.standardError || 0.1;
  const tValue = 1.96;  // 95%信頼区間

  // 信頼区間の計算（対数スケールで計算後、指数変換）
  const ld50LogSE = se / Math.abs(b);
  const ld50LogCI = tValue * ld50LogSE;
  const ld50CI = {
    lower: Math.pow(10, Math.log10(ld50) - ld50LogCI),
    upper: Math.pow(10, Math.log10(ld50) + ld50LogCI),
  };

  const ec50CI = ld50CI;  // EC50とLD50は同じ信頼区間

  const ec90LogCI = ld50LogCI;  // 簡易的に同じ幅を使用
  const ec90CI = {
    lower: Math.pow(10, Math.log10(ec90) - ec90LogCI),
    upper: Math.pow(10, Math.log10(ec90) + ec90LogCI),
  };

  return {
    slope: b,
    intercept: a,
    ld50,
    ec50,
    ec90,
    ld50CI,
    ec50CI,
    ec90CI,
    regression,
    isGLM: false, // 簡易版（ロジット変換+線形回帰）はGLMではない
  };
}

/**
 * 二項分布のGLM（ロジットリンク）によるロジスティック回帰
 * 
 * この実装は、死亡数/総数（n）を使用した統計学的に正確なロジスティック回帰です。
 * - 分布: 二項分布
 * - リンク関数: ロジット（Logit = ln(p / (1-p))）
 * - 推定方法: 最大尤度推定（Iteratively Reweighted Least Squares, IRLS）
 * - 信頼区間: Wald法による95%信頼区間
 * 
 * 参考文献:
 * - McCullagh, P., Nelder, J.A. (1989). Generalized Linear Models, 2nd ed. Chapman & Hall/CRC.
 * 
 * @param doses 用量（濃度）の配列
 * @param dead 死亡数の配列
 * @param total 総数の配列
 * @returns ロジスティック回帰の結果
 */
export function logisticRegressionGLM(
  doses: number[],
  dead: number[],
  total: number[]
): LogisticResult {
  if (doses.length !== dead.length || doses.length !== total.length || doses.length < 3) {
    throw new KeisukeAdapterError('minThreeDataPoints');
  }

  // データの検証
  for (let i = 0; i < doses.length; i++) {
    if (doses[i] <= 0) {
      throw new KeisukeAdapterError('doseMustBePositive', { row: i + 1 });
    }
    if (dead[i] < 0 || dead[i] > total[i]) {
      throw new KeisukeAdapterError('deadCountInvalid', { row: i + 1 });
    }
    if (total[i] <= 0) {
      throw new KeisukeAdapterError('totalMustBePositive', { row: i + 1 });
    }
  }

  const logDoses = doses.map(d => Math.log10(d));
  const responses = dead.map((d, i) => d / total[i]);

  // 初期値として簡易ロジスティック回帰の結果を使用
  const initialLogits = responses.map(p => logitTransform(p));
  const initialRegression = linearRegression(logDoses, initialLogits);
  let a = initialRegression.intercept;
  let b = initialRegression.slope;

  // 最尤推定（重み付き反復再重み付け最小二乗法、IRLS）
  const maxIterations = 50;
  const tolerance = 1e-6;

  for (let iter = 0; iter < maxIterations; iter++) {
    let sumW = 0;
    let sumWX = 0;
    let sumWY = 0;
    let sumWXY = 0;
    let sumWX2 = 0;

    for (let i = 0; i < doses.length; i++) {
      const logDose = logDoses[i];
      const n = total[i];
      const y = dead[i];

      // 線形予測子: eta = a + b * logDose
      const eta = a + b * logDose;
      // ロジットリンクの逆変換: p = 1 / (1 + exp(-eta))
      const pPredicted = 1 / (1 + Math.exp(-eta));

      // ロジットリンクの導関数: d(eta)/d(p) = 1 / (p * (1-p))
      // 重み: w = n * p * (1-p)（二項分布の分散を考慮）
      const weight = n * pPredicted * (1 - pPredicted);

      // 作業変数: z = eta + (y - n*p) / (n * p * (1-p))
      const workingY = eta + (y - n * pPredicted) / (n * pPredicted * (1 - pPredicted) + 1e-10);

      sumW += weight;
      sumWX += weight * logDose;
      sumWY += weight * workingY;
      sumWXY += weight * logDose * workingY;
      sumWX2 += weight * logDose * logDose;
    }

    // 重み付き最小二乗法でパラメータを更新
    const det = sumW * sumWX2 - sumWX * sumWX;
    if (Math.abs(det) < 1e-10) {
      break; // 行列が特異
    }

    const aNew = (sumWY * sumWX2 - sumWXY * sumWX) / det;
    const bNew = (sumW * sumWXY - sumWX * sumWY) / det;

    // 収束判定
    if (Math.abs(aNew - a) < tolerance && Math.abs(bNew - b) < tolerance) {
      a = aNew;
      b = bNew;
      break;
    }

    a = aNew;
    b = bNew;
  }

  if (Math.abs(b) < 1e-10) {
    throw new KeisukeAdapterError('slopeNearZero');
  }

  // LD50/EC50の計算: LD50/EC50 = 10^(-a/b)
  // ロジット値0が50%に対応
  const ld50 = Math.pow(10, -a / b);
  const ec50 = ld50;

  // EC90の計算: EC90 = 10^(-(a + ln(9))/b)
  // ロジット値ln(9)が90%に対応
  const logit90 = Math.log(9);
  const ec90 = Math.pow(10, -(a + logit90) / b);

  // 信頼区間の計算（Wald法）
  // パラメータの分散共分散行列を計算
  let sumW = 0;
  let sumWX = 0;
  let sumWX2 = 0;

  for (let i = 0; i < doses.length; i++) {
    const logDose = logDoses[i];
    const n = total[i];
    const eta = a + b * logDose;
    const pPredicted = 1 / (1 + Math.exp(-eta));
    const weight = n * pPredicted * (1 - pPredicted);

    sumW += weight;
    sumWX += weight * logDose;
    sumWX2 += weight * logDose * logDose;
  }

  const det = sumW * sumWX2 - sumWX * sumWX;
  const varA = sumWX2 / det;
  const varB = sumW / det;
  const covAB = -sumWX / det;

  // LD50の対数スケールでの標準誤差（Delta法）
  // Var(log10(LD50)) = Var(-a/b) ≈ (1/b²) * (Var(a) + (a/b)² * Var(b) - 2*a/b * Cov(a,b))
  const ld50LogVar = (1 / (b * b)) * (varA + Math.pow(a / b, 2) * varB - 2 * a / b * covAB);
  const ld50LogSE = Math.sqrt(Math.max(0, ld50LogVar));
  const zValue = 1.96; // 95%信頼区間

  const ld50LogCI = zValue * ld50LogSE;
  const ld50CI = {
    lower: Math.pow(10, Math.log10(ld50) - ld50LogCI),
    upper: Math.pow(10, Math.log10(ld50) + ld50LogCI),
  };

  const ec50CI = ld50CI;

  // EC90の信頼区間（Delta法）
  const ec90LogVar = (1 / (b * b)) * (varA + Math.pow((a + logit90) / b, 2) * varB - 2 * (a + logit90) / b * covAB);
  const ec90LogSE = Math.sqrt(Math.max(0, ec90LogVar));
  const ec90LogCI = zValue * ec90LogSE;
  const ec90CI = {
    lower: Math.pow(10, Math.log10(ec90) - ec90LogCI),
    upper: Math.pow(10, Math.log10(ec90) + ec90LogCI),
  };

  // 回帰結果
  const regression: LinearRegressionResult = {
    slope: b,
    intercept: a,
    rSquared: 0, // GLMではR²は定義が異なるため、ここでは0とする
    n: doses.length,
    standardError: ld50LogSE,
    slopeSE: Math.sqrt(varB),
    interceptSE: Math.sqrt(varA),
  };

  return {
    slope: b,
    intercept: a,
    ld50,
    ec50,
    ec90,
    ld50CI,
    ec50CI,
    ec90CI,
    regression,
    isGLM: true, // GLMを使用
  };
}

